import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";
import "./OwnerSettings.css";
import { formatProfessionalType } from "../../utils/formatters";

function ProfessionalProfileSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [payoutPreference, setPayoutPreference] = useState("business-revenue");
  const [savingPreference, setSavingPreference] = useState(false);

  useEffect(() => {
    fetchUserInfo();
    fetchProfessionalTypes();
    fetchLocations();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUserInfo(response.data);

      // Fetch staff record to get payout preference if professional profile is enabled
      if (
        response.data.roles &&
        (response.data.roles.includes("R3") ||
          response.data.roles.includes("R4"))
      ) {
        fetchPayoutPreference();
      }
    } catch (error) {
      setError("Failed to load user information");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutPreference = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/staff/me`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Set payout preference, defaulting to 'business-revenue' if null/undefined
      const preference =
        response.data.staff?.ownerPayoutPreference || "business-revenue";
      setPayoutPreference(preference);
    } catch (error) {
    }
  };

  const handleSavePayoutPreference = async () => {
    setSavingPreference(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/owners/payout-preference`,
        { ownerPayoutPreference: payoutPreference },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess("Payout preference updated successfully");

      // Refresh the preference from server to confirm it was saved
      await fetchPayoutPreference();

      if (response.data.requiresBankAccount) {
        setError(
          "Please connect your personal bank account in your professional profile to receive payouts",
        );
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to update payout preference",
      );
    } finally {
      setSavingPreference(false);
    }
  };

  const fetchProfessionalTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/public`,
      );
      setProfessionalTypes(response.data.professionalTypes || []);
    } catch (error) {
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/shop/my-shops`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const userShops = Array.isArray(response.data)
        ? response.data
        : response.data.shops || [];
      setLocations(userShops);
      // Auto-select if only one location
      if (userShops.length === 1) {
        setSelectedLocation(userShops[0]._id);
      }
    } catch (error) {
    }
  };

  const handleEnableProfessionalProfile = async () => {
    if (!selectedType) {
      setError("Please select a professional type");
      return;
    }

    if (!selectedLocation) {
      setError("Please select a location");
      return;
    }

    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/owners/enable-professional-profile`,
        {
          professionalType: selectedType,
          locationId: selectedLocation,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update token with new roles
      if (response.data.token) {
        localStorage.setItem("ogestyle_token", response.data.token);
        localStorage.setItem(
          "ogestyle_roles",
          JSON.stringify(response.data.user.roles),
        );
      }

      setSuccess("Professional profile enabled successfully! Refreshing...");
      setShowEnableModal(false);

      // Reload the current page to reflect new roles (stay on settings page)
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to enable professional profile",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleDisableProfessionalProfile = async () => {
    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/owners/disable-professional-profile`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update token with new roles
      if (response.data.token) {
        localStorage.setItem("ogestyle_token", response.data.token);
        localStorage.setItem(
          "ogestyle_roles",
          JSON.stringify(response.data.user.roles),
        );
      }

      setSuccess("Professional profile archived successfully! Refreshing...");
      setShowDisableConfirm(false);

      // Reload the current page to reflect new roles (stay on settings page)
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to disable professional profile",
      );
    } finally {
      setProcessing(false);
    }
  };

  const hasProfessionalProfile =
    userInfo?.roles?.includes("R3") || userInfo?.roles?.includes("R4");
  const roles = Array.isArray(userInfo?.roles) ? userInfo.roles : [];
  const hasDualOwnerProfessionalRoles =
    roles.includes("R1") && roles.includes("R3");
  const currentRole =
    localStorage.getItem("ogestyle_role_override") ||
    localStorage.getItem("ogestyle_role") ||
    "";
  const shouldHidePayoutPreferenceControls =
    hasDualOwnerProfessionalRoles && currentRole === "R3";

  if (loading) {
    return (
      <SidebarLayout>
        <div className="settings-container settings-page">
          <p>Loading...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="settings-container settings-page">
        <h1 className="settings-title">Professional Profile Settings</h1>

        {error && <div className="alert alert-error">{error}</div>}

        {success && <div className="alert alert-success">{success}</div>}

        <div className="settings-card">
          <div className="card-header">
            <h2>Professional Profile Status</h2>
          </div>

          <div className="card-body">
            {hasProfessionalProfile ? (
              <div className="status-section">
                <div className="status-badge status-active">
                  ✓ Professional Profile Enabled
                </div>
                <p className="status-description">
                  You have an active professional profile as a{" "}
                  <strong>
                    {formatProfessionalType(userInfo.professionalType) ||
                      "Professional"}
                  </strong>
                  . You can accept appointments and offer services at your
                  business.
                </p>
                <button
                  className="btn-danger"
                  onClick={() => setShowDisableConfirm(true)}
                  disabled={processing}
                >
                  Archive Professional Profile
                </button>
              </div>
            ) : (
              <div className="status-section">
                <div className="status-badge status-inactive">
                  ⚠️ Professional Profile Not Enabled
                </div>
                <p className="status-description">
                  Enable your professional profile to work at your business,
                  accept appointments, and offer services directly to customers.
                </p>
                <button
                  className="btn-primary"
                  onClick={() => setShowEnableModal(true)}
                  disabled={processing}
                >
                  Enable Professional Profile
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Owner Payout Preference - only show if professional profile is enabled */}
        {hasProfessionalProfile && !shouldHidePayoutPreferenceControls && (
          <div className="settings-card">
            <div className="card-header">
              <h2>Payout Preference</h2>
            </div>
            <div className="card-body">
              <p style={{ color: "#6b7280", marginBottom: "16px" }}>
                Choose how you want to receive your professional earnings
              </p>

              <div className="payout-preference-options">
                <label
                  className={`preference-option ${payoutPreference === "business-revenue" ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="payoutPreference"
                    value="business-revenue"
                    checked={payoutPreference === "business-revenue"}
                    onChange={(e) => setPayoutPreference(e.target.value)}
                    disabled={savingPreference}
                  />
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-icon">🏢</span>
                      <strong>Business Revenue (Recommended)</strong>
                    </div>
                    <p className="option-description">
                      Your professional earnings stay in your business account.
                      Access via business distributions.
                    </p>
                    <ul className="option-benefits">
                      <li>✓ No bank account connection needed</li>
                      <li>✓ Simpler tax reporting</li>
                      <li>✓ No transfer fees</li>
                      <li>✓ Earnings remain as business capital</li>
                    </ul>
                  </div>
                </label>

                <label
                  className={`preference-option ${payoutPreference === "personal-payout" ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="payoutPreference"
                    value="personal-payout"
                    checked={payoutPreference === "personal-payout"}
                    onChange={(e) => setPayoutPreference(e.target.value)}
                    disabled={savingPreference}
                  />
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-icon">💳</span>
                      <strong>Personal Payout</strong>
                    </div>
                    <p className="option-description">
                      Receive payouts every 36 hours to your personal bank
                      account, just like other staff members.
                    </p>
                    <ul className="option-benefits">
                      <li>✓ Separate professional income</li>
                      <li>✓ Automatic transfers every 36 hours</li>
                      <li>✓ Clear personal vs business separation</li>
                      <li>⚠️ Requires personal bank account connection</li>
                    </ul>
                  </div>
                </label>
              </div>

              <button
                className="btn-primary"
                onClick={handleSavePayoutPreference}
                disabled={savingPreference}
                style={{ marginTop: "16px" }}
              >
                {savingPreference ? "Saving..." : "Save Preference"}
              </button>
            </div>
          </div>
        )}

        <div className="settings-card">
          <div className="card-header">
            <h2>What is a Professional Profile?</h2>
          </div>
          <div className="card-body">
            <p>
              A professional profile allows you as the business owner to also
              work as a service provider at your own business. When enabled:
            </p>
            <ul className="benefits-list">
              <li>✓ Accept bookings and appointments from customers</li>
              <li>✓ Offer your own services and set your availability</li>
              <li>✓ Manage your professional schedule separately</li>
              <li>✓ Switch between owner and professional dashboards</li>
              <li>✓ Track your earnings and performance</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Enable Professional Profile Modal */}
      {showEnableModal && (
        <div
          className="modal-overlay"
          onClick={() => !processing && setShowEnableModal(false)}
        >
          <div
            className="modal-content-pro"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title">Enable Professional Profile</h2>
            <p className="modal-subtitle">
              Select your professional type and location where you'll work.
            </p>

            {error && <div className="error-message">{error}</div>}

            <div className="form-section">
              <label className="form-label">Select Location</label>
              <select
                className="location-select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="">Choose a location...</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.name} - {loc.city}, {loc.state}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-section">
              <label className="form-label">Select Professional Type</label>
              <div className="professional-types-grid">
                {professionalTypes.map((type) => (
                  <div
                    key={type.value}
                    className={`professional-type-option ${selectedType === type.value ? "selected" : ""}`}
                    onClick={() => setSelectedType(type.value)}
                  >
                    <div className="type-icon">{type.icon}</div>
                    <div className="type-label">{type.label}</div>
                    {type.description && (
                      <div className="type-description">{type.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowEnableModal(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleEnableProfessionalProfile}
                disabled={processing || !selectedType || !selectedLocation}
              >
                {processing ? "Enabling..." : "Enable Profile"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div
          className="modal-overlay"
          onClick={() => !processing && setShowDisableConfirm(false)}
        >
          <div
            className="modal-content-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title">Archive Professional Profile?</h2>
            <p className="modal-subtitle">
              Your professional profile will be archived (not deleted). All your
              data including services, schedule, ratings, and client history
              will be preserved. You can reactivate it anytime without losing
              any information.
            </p>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowDisableConfirm(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleDisableProfessionalProfile}
                disabled={processing}
              >
                {processing ? "Archiving..." : "Yes, Archive Profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
}

export default ProfessionalProfileSettings;
